import { useCallback, useState } from 'react'

import { useUnit } from 'effector-react'

import ExpandIcon from '@/assets/icons/icon-expand-double.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import {
  $creators,
  $currentCreator,
  $isCreatorProcessCompleted,
  setCreatorId,
} from '@/models/creators.ts'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'
import { classNames } from '@/utils/helpers'

import { isActiveCreator } from '../utils'

export const CreatorSelector = () => {
  const [open, setOpen] = useState(false)
  const creators = useUnit($creators)
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)
  const activeCreator = useUnit($currentCreator)

  const handleCreatorChange = useCallback((id: string) => {
    setCreatorId(parseInt(id))
    setOpen(false)
  }, [])

  // https://github.com/pacocoursey/cmdk#command-cmdk-root
  const filterSearchInput = useCallback(
    (creatorId: string, searchInput: string): number => {
      const creator = creators.find(
        (creator) => creator.id.toString() === creatorId,
      )

      if (!creator) {
        return 0
      }

      const normalizedCreatorName = creator.name.toLowerCase()

      if (normalizedCreatorName.includes(searchInput.toLowerCase())) {
        return 1
      }

      return 0
    },
    [creators],
  )

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          testId="creator-selector"
          data-creator-id={activeCreator?.id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Select a creator"
          className="bg-background flex w-full items-center justify-between gap-3 rounded-lg px-5 py-7"
        >
          <div className="flex items-center gap-1">
            {activeCreator && (
              <CreatorAvatar
                key={activeCreator.id}
                className="h-5 w-5"
                name={activeCreator.name}
                avatarSrc={activeCreator.avatar?.croppedUrl}
              />
            )}

            <span data-testid="creator-name">
              {!isCreatorProcessCompleted && 'Loading...'}
              {isCreatorProcessCompleted &&
                !activeCreator &&
                'No creator selected'}
              {activeCreator?.name}
            </span>
          </div>

          <ExpandIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        sideOffset={8}
        className="w-[255px] p-0"
      >
        <Command filter={filterSearchInput}>
          <CommandList data-testid="creators-selector-list">
            <CommandInput
              placeholder="Search Creator"
              data-testid="creator-selector-search"
            />
            <CommandEmpty>No Creator found.</CommandEmpty>
            {creators?.filter(isActiveCreator).map((creator) => (
              <CommandItem
                key={creator.name}
                onSelect={handleCreatorChange}
                value={String(creator.id)}
                className={classNames('cursor-pointer px-5 py-3 text-sm', {
                  'bg-primary-500 aria-selected:bg-primary-500 dark:aria-selected:bg-primary-500 text-white':
                    activeCreator?.id === creator.id,
                })}
              >
                <CreatorAvatar
                  name={creator.name}
                  avatarSrc={creator.avatar?.croppedUrl}
                  className="mr-2 h-5 w-5"
                />
                <span data-testid="creator-selector-option">
                  {creator.name}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
